<template>
  <div class="panel" id="income-panel">
    <p v-if="!hidePanelHeading" class="panel-heading columnhead">Your Household Retirement Income</p>
    <div class="panel-block">
      <section class="content-section-full">
        <div class="columns">
          <div class="column" :class="{ 'focus': isIncomeEdit }" :style="[isIncomeEdit ? focusStyle : {}]">
            <div id="income-ss">
            <label class="label">Monthly Social Security benefit <b-tooltip type="is-info" label="This amount can be obtained from your Social Security Statement. Register at www.SSA.gov/myaccount to view your Social Security Statement. Enter your full retirement age benefit here." position="is-left" multilined><i class="fas fa-info-circle moreinfo"></i></b-tooltip></label>
            <b-field>
              <cleave class="input control" placeholder="Enter Social Security amount at Full Retirement Age" v-model.number="clientSocialSecurityBenefitAmountInput" data-vv-name="clientSocialSecurityBenefitAmountInput" v-validate="options.v_number_required" :options="options.number"></cleave>
            </b-field>
            <span v-show="errors.has('clientSocialSecurityBenefitAmountInput')" class="help is-danger"><font-awesome-icon icon="exclamation-triangle" /> {{ errors.first('clientSocialSecurityBenefitAmountInput') }}</span>
            <div v-show="isMarried">
              <b-field label="What is your partner's monthly Social Security benefit?">
                <cleave class="input" placeholder="Enter Social Security amount at Full Retirement Age" v-model.number="spouseSocialSecurityBenefitAmountInput" data-vv-name="spouseSocialSecurityBenefitAmountInput" v-validate="{ rules: { required: spouseRequired } }" :options="options.number"></cleave>
              </b-field>
              <span v-show="errors.has('spouseSocialSecurityBenefitAmountInput')" class="help is-danger"><font-awesome-icon icon="exclamation-triangle" /> {{ errors.first('spouseSocialSecurityBenefitAmountInput') }}</span>
            </div>
            </div>
            <div id="income-pension">
            <label class="label">Monthly retirement pension (if applicable)</label>
            <b-field>
              <cleave class="input" placeholder="$0" v-model.number="clientMonthlyPensionInput" data-vv-name="clientMonthlyPensionInput" v-validate="'min_value:0|numeric'" :options="options.number"></cleave>
            </b-field>
            <span v-show="errors.has('clientMonthlyPensionInput')" class="help is-danger"><font-awesome-icon icon="exclamation-triangle" /> {{ errors.first('clientMonthlyPensionInput') }}</span>
            </div>
            <label class="label">Any additional monthly retirement income <b-tooltip type="is-info" label="Include monthly annuity payments if applicable, rental income, or any income expected to continue indefinitely." position="is-left" multilined><i class="fas fa-info-circle moreinfo"></i></b-tooltip></label>
            <b-field>
              <cleave class="input" placeholder="" v-model.number="clientOtherMonthlyIncomeInput" data-vv-name="clientOtherMonthlyIncomeInput" v-validate="'min_value:0|numeric'" :options="options.number"></cleave>
            </b-field>
            <span v-show="errors.has('clientOtherMonthlyIncomeInput')" class="help is-danger"><font-awesome-icon icon="exclamation-triangle" /> {{ errors.first('clientOtherMonthlyIncomeInput') }}</span>
          </div>
        </div>
      </section>
    </div>
  </div>
  <!-- / div END -->
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  inject: ['$validator'],
  props: {
    hidePanelHeading: Boolean
  },
  data () {
    return {
      clientSocialSecurityBenefitAmountInput: null,
      spouseSocialSecurityBenefitAmountInput: null,
      clientOtherMonthlyIncomeInput: null,
      clientMonthlyPensionInput: null,
      options: {
        number: {
          prefix: '$',
          numeral: true,
          numeralPositiveOnly: true,
          noImmediatePrefix: true,
          rawValueTrimPrefix: true,
          numeralIntegerScale: 9,
          numeralDecimalScale: 0
        },
        v_number_required: {
          required: true
        }
      },
      focusStyle: {
        'padding': '10px',
        'border-radius': '4px'
      }
    }
  },
  watch: {
    clientSocialSecurityBenefitAmountInput (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateClientSocialSecurityBenefitAmount(newValue)
      }
    },
    clientSocialSecurityBenefitAmount: function () {
      this.clientSocialSecurityBenefitAmountInput = this.clientSocialSecurityBenefitAmount
    },
    spouseSocialSecurityBenefitAmountInput (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updatePartnerSocialSecurityBenefitAmount(newValue)
      }
    },
    clientMonthlyPensionInput (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateClientMonthlyPension(newValue)
      }
    },
    clientOtherMonthlyIncomeInput (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateClientOtherMonthlyIncome(newValue)
      }
    }
  },
  computed: {
    ...mapGetters([
      'clientSocialSecurityBenefitAmount',
      'partnerSocialSecurityBenefitAmount',
      'clientMonthlyPension',
      'clientOtherMonthlyIncome',
      'isMarried',
      'getEditMode'
    ]),
    spouseRequired: function () {
      if (this.married === 'true') {
        return true
      } else {
        return false
      }
    },
    isIncomeEdit: function () {
      return (this.getEditMode === 'income')
    }
  },
  methods: {
    ...mapActions({
      updateClientSocialSecurityBenefitAmount: 'updateClientSocialSecurityBenefitAmount',
      updatePartnerSocialSecurityBenefitAmount: 'updatePartnerSocialSecurityBenefitAmount',
      updateClientMonthlyPension: 'updateClientMonthlyPension',
      updateClientOtherMonthlyIncome: 'updateClientOtherMonthlyIncome'
    })
  },
  mounted () {
    this.clientSocialSecurityBenefitAmountInput = this.clientSocialSecurityBenefitAmount
    this.spouseSocialSecurityBenefitAmountInput = this.partnerSocialSecurityBenefitAmount
    this.clientMonthlyPensionInput = this.clientMonthlyPension
    this.clientOtherMonthlyIncomeInput = this.clientOtherMonthlyIncome
  },
  created () {
    this.clientSocialSecurityBenefitAmountInput = this.clientSocialSecurityBenefitAmount
    this.spouseSocialSecurityBenefitAmountInput = this.partnerSocialSecurityBenefitAmount
    this.clientMonthlyPensionInput = this.clientMonthlyPension
    this.clientOtherMonthlyIncomeInput = this.clientOtherMonthlyIncome
  }
}
</script>

<style scoped>
#income-ss{
  margin-bottom:25px;
}
#income-pension{
  margin-bottom:25px;
}
</style>
